import { useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FacebookIcon from "@mui/icons-material/Facebook";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useTranslation } from "i18n";

import Button from "components/button";
import { getJobByID } from "api/Jobs";
import useApi from "hooks/useApi";
import { isUserLoggedIn } from "hooks/LocalStorage";
import { renderJobMarkdown } from "utils/jobMarkdown";
import ApplicationStepper from "./components/applicationStepper";

import "./style.css";

function JobDetails() {
  const { t, locale } = useTranslation();
  const applyRef = useRef(null);
  const { jobId } = useParams();

  const getJobDetails = useApi(getJobByID);
  const language = locale === "nl" ? "nl" : "en";
  const job = getJobDetails?.data?.[language] || getJobDetails?.data?.en;

  useEffect(() => {
    window.scrollTo(0, 0);
    getJobDetails.request({ jobId });
  }, [jobId]);

  const scrollToApply = () => {
    if (!isUserLoggedIn()) {
      window.location.hash = "#auth-required";
    }
    if (!applyRef.current) return;
    const top =
      applyRef.current.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const shareUrl =
    typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = job?.title
    ? `${job.title} · Satyam`
    : "Satyam Careers";

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(
      `${t("jobs.shareEmailBody")}\n\n${shareUrl}`,
    )}`,
  };

  const metaItems = [
    job?.type && {
      icon: WorkOutlineRoundedIcon,
      label: job.type,
    },
    job?.location && {
      icon: LocationOnOutlinedIcon,
      label: job.location,
    },
    job?.department && {
      icon: GroupsOutlinedIcon,
      label: job.department,
    },
  ].filter(Boolean);

  return (
    <div className="job-detail-page">
      <section className="job-detail-hero">
        <div className="job-detail-hero__media" aria-hidden="true">
          <img src={require("assets/images/job-banner.png")} alt="" />
        </div>
        <div className="job-detail-hero__veil" aria-hidden="true" />
        <div className="container job-detail-hero__content">
          <Link to="/jobs" className="job-detail-back">
            <ArrowBackRoundedIcon fontSize="small" />
            {t("jobs.backToJobs")}
          </Link>

          {getJobDetails?.loading ? (
            <>
              <Skeleton
                variant="text"
                width={120}
                height={36}
                sx={{ bgcolor: "rgba(255,255,255,0.25)" }}
              />
              <Skeleton
                variant="text"
                width="70%"
                height={48}
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
              <Skeleton
                variant="rounded"
                width={280}
                height={28}
                sx={{ bgcolor: "rgba(255,255,255,0.2)", mt: 1 }}
              />
            </>
          ) : getJobDetails?.error ? (
            <>
              <h1 className="job-detail-hero__title">
                {t("common.genericError")}
              </h1>
            </>
          ) : (
            <>
              <h1 className="job-detail-hero__title">{job?.title}</h1>
              {metaItems.length > 0 && (
                <div className="job-detail-hero__meta">
                  {metaItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <span key={item.label}>
                        <Icon fontSize="inherit" />
                        {item.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <div className="container job-detail-shell">
        {getJobDetails?.loading ? (
          <div className="job-detail-loading">
            <div className="job-detail-panel">
              <Skeleton variant="rounded" height={22} width="36%" />
              <Skeleton variant="rounded" height={16} width="90%" sx={{ mt: 2 }} />
              <Skeleton variant="rounded" height={16} width="84%" sx={{ mt: 1 }} />
              <Skeleton variant="rounded" height={16} width="72%" sx={{ mt: 1 }} />
            </div>
            <div className="job-detail-panel">
              <Skeleton variant="rounded" height={180} />
            </div>
          </div>
        ) : getJobDetails?.error ? (
          <div className="job-detail-empty">
            <h2>{t("common.genericError")}</h2>
            <p>{t("jobs.errorMessage")}</p>
            <Link to="/jobs" className="job-detail-empty__link">
              {t("jobs.backToJobs")}
            </Link>
          </div>
        ) : (
          <div className="job-detail-layout">
            <div className="job-detail-main">
              {(job?.jobDescription || metaItems.length > 0) && (
                <section className="job-detail-panel job-detail-overview">
                  <h2>{t("jobs.roleOverview")}</h2>
                  {job?.jobDescription && (
                    <div className="job-detail-overview__desc">
                      <ArticleOutlinedIcon fontSize="small" />
                      <div
                        className="job-detail-markdown"
                        dangerouslySetInnerHTML={{
                          __html: renderJobMarkdown(job.jobDescription),
                        }}
                      />
                    </div>
                  )}
                  <ul className="job-detail-overview__list">
                    {metaItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={`overview-${item.label}`}>
                          <Icon fontSize="small" />
                          <span>{item.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {job?.contents?.map((item, index) => (
                <section
                  className="job-detail-panel job-detail-section"
                  key={`${item?.title}-${index}`}
                >
                  <h2>{item?.title}</h2>
                  <ul>
                    {item?.content?.map((line, lineIndex) => (
                      <li
                        key={lineIndex}
                        className="job-detail-markdown"
                        dangerouslySetInnerHTML={{
                          __html: renderJobMarkdown(line),
                        }}
                      />
                    ))}
                  </ul>
                </section>
              ))}

              <section
                ref={applyRef}
                className="job-detail-panel job-detail-apply"
                id="apply"
              >
                <ApplicationStepper job={job} careerId={jobId} />
              </section>

              <aside className="job-detail-panel job-detail-note">
                <p>
                  <span>~</span> {t("jobs.diversityNote")}
                </p>
              </aside>
            </div>

            <aside className="job-detail-aside">
              <div className="job-detail-aside__card">
                <h3>{job?.title}</h3>
                <div className="job-detail-aside__meta">
                  {job?.department && <span>{job.department}</span>}
                  {job?.location && <span>{job.location}</span>}
                </div>
                <Button
                  className="primary-color job-detail-aside__cta"
                  onClick={scrollToApply}
                >
                  {t("jobs.applyNow")}
                </Button>

                <div className="job-detail-share">
                  <p>{t("jobs.shareListing")}</p>
                  <div>
                    <IconButton
                      component="a"
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="job-detail-share__btn"
                    >
                      <FacebookIcon />
                    </IconButton>
                    <IconButton
                      component="a"
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="job-detail-share__btn"
                    >
                      <LinkedInIcon />
                    </IconButton>
                    <IconButton
                      component="a"
                      href={shareLinks.email}
                      aria-label="Email"
                      className="job-detail-share__btn"
                    >
                      <EmailIcon />
                    </IconButton>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export default JobDetails;
